import { createSelector } from 'reselect'
import { immer } from 'zustand/middleware/immer'

import { pages } from '~/app/routes'

import { StoreState } from '../store'
import { BaseFilter, createFilterSliceActions, FilterSliceActions } from './types'

export const waitingListAllAndScheduledFilters = [
    'asa',
    'day-overnight',
    'cleared',
    'confirmed',
    'short-notice',
    'patient-availability-from',
    'patient-availability-to',
    'prio',
    'waiting',
    'practitioner-combo',
    'surgery-category',
    'diagnosis-group',
    'letter-status',
    'infection',
    'surgery-cancer',
    'metadata-surgery-suggestion',
    'follow-up',
    'has-planning-note',
    'planning-note-empty',
    'has-coordination-comment',
    'planning-note-same-as-coordination-comment',
    'planning-note-different-from-coordination-comment',
    'not-migrated',
    'migrated',
    'needs-remigration',
    'planning-note-without-merge-separator',
    'planning-note-multiple-merge-separators',
    'surgery-type-same-as-planning-note',
    'surgery-type-included-in-planning-note',
    'surgery-type-different-from-planning-note',
] as const

export const waitingListUnscheduledFilters = [
    'asa',
    'day-overnight',
    'cleared',
    'short-notice',
    'patient-availability-from',
    'patient-availability-to',
    'prio',
    'waiting',
    'practitioner-combo',
    'surgery-category',
    'diagnosis-group',
    'infection',
    'surgery-cancer',
    'metadata-surgery-suggestion',
    'follow-up',
    'has-planning-note',
    'planning-note-empty',
    'has-coordination-comment',
    'planning-note-same-as-coordination-comment',
    'planning-note-different-from-coordination-comment',
    'not-migrated',
    'migrated',
    'needs-remigration',
    'planning-note-without-merge-separator',
    'planning-note-multiple-merge-separators',
    'surgery-type-same-as-planning-note',
    'surgery-type-included-in-planning-note',
    'surgery-type-different-from-planning-note',
] as const

export type WaitingListFilterType = (typeof waitingListAllAndScheduledFilters)[number] | (typeof waitingListUnscheduledFilters)[number]

export type WaitingListFilter = {
    type: WaitingListFilterType
} & BaseFilter

type State = {
    filters: WaitingListFilter[]
}

type Actions = {
    actions: FilterSliceActions<WaitingListFilterType>
}

export type WaitingListFilterSlice = {
    waitingListFilters: State & Actions
}

const initialState: State = {
    filters: [],
}

export const createWaitingListFilterSlice = immer<WaitingListFilterSlice>((set, get) => ({
    waitingListFilters: {
        ...initialState,
        actions: createFilterSliceActions<WaitingListFilterType, WaitingListFilter>('waitingListFilters', set, get),
    },
}))

const selectAllFilters = (state: StoreState) => state.waitingListFilters.filters

// View specific selector
export const selectWaitingListFiltersForView = createSelector(
    selectAllFilters,
    (state: StoreState) => state.app.activeViews.WAITING_LIST,
    (filters, activeView) =>
        filters.filter((filter: WaitingListFilter) => {
            if (activeView === pages.WAITING_LIST.views.unscheduled.path) {
                return (waitingListUnscheduledFilters as readonly string[]).includes(filter.type)
            } else {
                return (waitingListAllAndScheduledFilters as readonly string[]).includes(filter.type)
            }
        })
)

// Filter specific selectors
export const selectClearedValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'cleared').map((filter: WaitingListFilter) => filter.value)
)

export const selectConfirmedValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'confirmed').map((filter: WaitingListFilter) => filter.value)
)

export const selectShortNoticeValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'short-notice').map((filter: WaitingListFilter) => filter.value)
)

export const selectPrioritizationValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'prio').map((filter: WaitingListFilter) => filter.value)
)

export const selectWaitingValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'waiting').map((filter: WaitingListFilter) => filter.value)
)

export const selectASAValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'asa').map((filter: WaitingListFilter) => filter.value)
)

export const selectPatientAvailabilityFromFilter = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'patient-availability-from')
)

export const selectPatientAvailabilityToFilter = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'patient-availability-to')
)

export const selectDayOvernightValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'day-overnight').map((filter: WaitingListFilter) => filter.value)
)

export const selectDiagnosisGroupValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'diagnosis-group').map((filter: WaitingListFilter) => filter.value)
)

export const selectSurgeryCategoryValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'surgery-category').map((filter: WaitingListFilter) => filter.value)
)

export const selectPractitionerComboValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'practitioner-combo').map((filter: WaitingListFilter) => filter.value)
)

export const selectSurgeryLetterStatus = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'letter-status').map((filter: WaitingListFilter) => filter.value)
)

export const selectInfectionValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'infection').map((filter: WaitingListFilter) => filter.value)
)

export const selectCancerValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'surgery-cancer').map((filter: WaitingListFilter) => filter.value)
)

export const selectMetadataSurgerySuggestionValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'metadata-surgery-suggestion').map((filter: WaitingListFilter) => filter.value)
)

export const selectFollowUpValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'follow-up').map((filter: WaitingListFilter) => filter.value)
)

export const selectHasPlanningNoteValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'has-planning-note').map((filter: WaitingListFilter) => filter.value)
)

export const selectPlanningNoteEmptyValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'planning-note-empty').map((filter: WaitingListFilter) => filter.value)
)

export const selectHasCoordinationCommentValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'has-coordination-comment').map((filter: WaitingListFilter) => filter.value)
)

export const selectPlanningNoteSameAsCoordinationCommentValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'planning-note-same-as-coordination-comment').map((filter: WaitingListFilter) => filter.value)
)

export const selectPlanningNoteDifferentFromCoordinationCommentValues = createSelector(selectAllFilters, filters =>
    filters
        .filter((filter: WaitingListFilter) => filter.type === 'planning-note-different-from-coordination-comment')
        .map((filter: WaitingListFilter) => filter.value)
)

export const selectNotMigratedValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'not-migrated').map((filter: WaitingListFilter) => filter.value)
)

export const selectMigratedValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'migrated').map((filter: WaitingListFilter) => filter.value)
)

export const selectNeedsRemigrationValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'needs-remigration').map((filter: WaitingListFilter) => filter.value)
)

export const selectPlanningNoteWithoutMergeSeparatorValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'planning-note-without-merge-separator').map((filter: WaitingListFilter) => filter.value)
)

export const selectPlanningNoteMultipleMergeSeparatorsValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'planning-note-multiple-merge-separators').map((filter: WaitingListFilter) => filter.value)
)

export const selectSurgeryTypeSameAsPlanningNoteValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'surgery-type-same-as-planning-note').map((filter: WaitingListFilter) => filter.value)
)

export const selectSurgeryTypeIncludedInPlanningNoteValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'surgery-type-included-in-planning-note').map((filter: WaitingListFilter) => filter.value)
)

export const selectSurgeryTypeDifferentFromPlanningNoteValues = createSelector(selectAllFilters, filters =>
    filters.filter((filter: WaitingListFilter) => filter.type === 'surgery-type-different-from-planning-note').map((filter: WaitingListFilter) => filter.value)
)
